import * as React from 'react'
import { FC, useCallback, useMemo } from 'react'
import { Formik } from 'formik'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { selectCurrentUser } from '@user/store/authorization/userSlice'
import { updateNotification } from '@user/store/notifications/api'
import { INotification } from '@user/store/notifications/typings'
import { SideDrawerForm } from '@sales-center/components'
import { NotificationLabel } from '../../atoms'
import { RadioWithLabel } from './RadioWithLabel'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import RadioGroup from '@mui/material/RadioGroup'
import Button from '@mui/material/Button'
import { NotificationType } from '../../atoms/typings'

interface FormModel {
  receiveAgreementOnEverySignature: string
}

export const ManageSignedAgreementSlideout: FC<{
  notification: INotification
  onClose: () => void
  type: NotificationType
}> = ({ onClose, notification, type }) => {
  const dispatch = useAppDispatch()

  const { clientUrl } = useSelector(selectCurrentUser)

  const initialValues: FormModel = useMemo(
    () => ({
      receiveAgreementOnEverySignature:
        notification.receiveAgreementOnEverySignature === null
          ? 'false'
          : `${notification.receiveAgreementOnEverySignature}`,
    }),
    [notification.receiveAgreementOnEverySignature],
  )

  const onSubmit = useCallback(
    async (model: FormModel) => {
      await dispatch(
        updateNotification({
          ...notification,
          receiveAgreementOnEverySignature:
            model.receiveAgreementOnEverySignature == 'true',
          additionalEmailIds: null,
          salesTeamHierarchy: null,
        }),
      )
      onClose()
    },
    [dispatch, notification, onClose],
  )

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ submitForm, setFieldValue, values }) => (
        <SideDrawerForm onSave={submitForm} onCancel={onClose}>
          <Box p={2}>
            <NotificationLabel
              type={type}
              name={notification.notificationEventName}
              helperText={notification.helperText}
            />
          </Box>

          <Divider />

          <Box py={3} pl={2} pr={6}>
            <RadioGroup
              value={values.receiveAgreementOnEverySignature}
              onChange={(_, value) =>
                setFieldValue('receiveAgreementOnEverySignature', value)
              }
            >
              <RadioWithLabel
                label="Upon completion"
                helperText="Customer will receive a copy of the agreement once fully executed/complete."
                value="false"
              />

              <RadioWithLabel
                label="Before Completion"
                helperText="Customer will receive two copies of the agreement:  An initial copy when signing a partially completed agreement which needs additional parties to complete, and a finalized copy once the agreement is fully completed by all parties."
                value="true"
              />
            </RadioGroup>
          </Box>

          <Divider />

          <Box display="flex" alignContent="center" pt={3} pb={3}>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              style={{ margin: 'auto' }}
              type="link"
              href={`${clientUrl}/sysSetup/printSetup.asp`}
              target="_blank"
            >
              Manage the Customer Notification Settings for Non-Sales Center
              Forms
            </Button>
          </Box>
        </SideDrawerForm>
      )}
    </Formik>
  )
}
ManageSignedAgreementSlideout.displayName = 'ManageSignedAgreementSlideout'
